import { Button, Grid, InputAdornment, TextField } from "@mui/material";
import { EnhancedEncryption, Lock } from "@mui/icons-material";
import React, { useState, Dispatch, useEffect } from "react";
import { useSelector } from "react-redux";
import { 
  reauthenticateWithPhoneNumber, 
  reauthenticateWithCredential, 
  RecaptchaVerifier, 
  updatePassword, 
  EmailAuthProvider,
  AuthCredential
} from "firebase/auth";
import { useTranslation } from "react-i18next";

import { linkFakeEmail } from "../api";
import AppBar from "../components/AppBar";
import SimpleSnackBar, { SnackBarProp, variantIcon } from "../components/SimpleSnackBar";
import { AppState } from "../reducers";
import { GridCard, StyledContainer } from "../StyledComponents";
import { FirebaseAuthError } from "../types/Error";
import { firebaseAuth, firebaseUser } from "../firebase";

export const SettingsPage = (props: { setTitle: Dispatch<React.SetStateAction<string>>}) => { 

  useEffect(() => {
    props.setTitle("setting");
  }, []);

  const {t} = useTranslation();
  const loggedInUser = useSelector<AppState, firebaseUser | null>(state => state.loggedInUser);

  const closeErrorSnackBar = () => {
    setErrorSnackBar({ ...errorSnackBar, "open": false });
  }

  const [errorSnackBar, setErrorSnackBar] = useState<SnackBarProp>({
    open: false,
    variant: "error",
    onClose: closeErrorSnackBar,
    message: "",
  });

  const ChangePasswordPanel = (props: { email: string | null | undefined, triggerError: (variant: keyof typeof variantIcon, message: string) => void }) => {
    return (
      <Grid item md={12} sm={12} xs={12}>
        <GridCard title={t("setting.change_pw_title")} desc={t("setting.change_pw_desc")}>
          {
            (props.email)
              ?
              <ChangePasswordForm email={props.email} triggerError={props.triggerError} />
              :
              <div style={{ margin: 20 }}>{t("setting.change_pw_content")}</div>
          }
        </GridCard>
      </Grid>
    );
  }

  const ChangePasswordForm = (props: { email: string | null | undefined, triggerError: (variant: keyof typeof variantIcon, message: string) => void }) => {
    type ChangePasswordFormInterface = {
      oldPassword: {
        value: string,
        isError: boolean,
        reason: string | undefined,
        validateFunc: (form: ChangePasswordFormInterface, value: string) => boolean
      },
      newPassword: {
        value: string,
        isError: boolean,
        reason: string | undefined,
        validateFunc: (form: ChangePasswordFormInterface, value: string) => boolean
      },
  
      confirmNewPassword: {
        value: string,
        isError: boolean,
        reason: string | undefined,
        validateFunc: (form: ChangePasswordFormInterface, value: string) => boolean
      },
    }
  
    type ChangePasswordFormField = "oldPassword" | "newPassword" | "confirmNewPassword";
  
    const checkOldPassword = (form: ChangePasswordFormInterface, value: string) => {
      if (!validatePassword(value)) {
        setForm(f => { return { ...f, oldPassword: { ...f.oldPassword, isError: true, reason: t("error.pw_format") } } });
        return false;
      }
      else {
        setForm(f => { return { ...f, oldPassword: { ...f.oldPassword, isError: false, reason: undefined } } });
        return true;
      }
    }
  
    const checkNewPassword = (form: ChangePasswordFormInterface, value: string) => {
      if (!validatePassword(value)) {
        setForm(f => { return { ...f, newPassword: { ...f.newPassword, isError: true, reason: t("error.pw_format") } } });
        return false;
      }
      else if (value === form.oldPassword.value) {
        setForm(f => { return { ...f, newPassword: { ...f.newPassword, isError: true, reason: t("error.pw_new_not_equals") } } });
        return false;
      }
      else {
        setForm(f => { return { ...f, newPassword: { ...f.newPassword, isError: false, reason: undefined } } });
        return true;
      }
    }
  
    const checkConfirmNewPassword = (form: ChangePasswordFormInterface, value: string) => {
      if (!validatePassword(value)) {
        setForm(f => { return { ...f, confirmNewPassword: { ...f.confirmNewPassword, isError: true, reason: t("error.pw_format") } } });
        return false;
      }
      else if (value !== form.newPassword.value) {
        setForm(f => { return { ...f, confirmNewPassword: { ...f.confirmNewPassword, isError: true, reason: t("error.pw_confirm_not_match") } } });
        return false;
      }
      else {
        setForm(f => { return { ...f, confirmNewPassword: { ...f.confirmNewPassword, isError: false, reason: undefined } } });
        return true;
      }
    }
  
    const defaultChangePasswordForm = {
      oldPassword: {
        value: "",
        isError: false,
        reason: undefined,
        validateFunc: checkOldPassword
      },
      newPassword: {
        value: "",
        isError: false,
        reason: undefined,
        validateFunc: checkNewPassword
      },
      confirmNewPassword: {
        value: "",
        isError: false,
        reason: undefined,
        validateFunc: checkConfirmNewPassword
      }
    };
  
    const [form, setForm] = useState<ChangePasswordFormInterface>(defaultChangePasswordForm);
  
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
  
    const onSubmitButtonClick = () => {
      const validateResult = Object.entries(form).map(([fieldId, fieldProps]) => {
        return fieldProps.validateFunc(form, fieldProps.value);
      });
  
      setIsSubmiting(true);
  
      if (validateResult.some(result => result === false)) {
        props.triggerError("error", t("setting.error_text_form_error"));
        setIsSubmiting(false);
        return;
      }
  
      if (firebaseAuth.currentUser !== undefined && firebaseAuth.currentUser !== null && props.email) {
        const reauthCredential: AuthCredential = EmailAuthProvider.credential(
          props.email,
          form.oldPassword.value
        );
  
        reauthenticateWithCredential(firebaseAuth.currentUser!, reauthCredential)
          .then(async (credential: any) => {
            try {
              await updatePassword(firebaseAuth.currentUser!, form.newPassword.value);
            }
            catch (e) {
              props.triggerError("error", t("setting.error_text_pw_update_error"));
              return;
            }
  
            props.triggerError("success", t("setting.success_text_pw_uodate"));
            setIsSubmiting(false);
            firebaseAuth.signOut();
          })
          .catch((err: any) => {
            const firebaseAuthError = err as FirebaseAuthError;
  
            let message = "";
            switch (firebaseAuthError.code) {
              case "auth/wrong-password": message = t("setting.error_text_pw_old_wrong"); break;
              case "auth/user-mismatch": message = t("setting.error_text_acc_wrong"); break;
              default: message = t("error.unknown"); break;
            }
  
            props.triggerError("error", message);
            setIsSubmiting(false);
            return;
          })
      }
      else {
        props.triggerError("error", t("setting.error_text_page_error"));
        return;
      }
    }
  
    const onFormChanged = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const fieldName = e.target.name;
      const fieldValue = e.target.value;
  
      setForm(f => { return { ...f, [fieldName]: { ...f[fieldName as ChangePasswordFormField], value: fieldValue } } })
      form[fieldName as ChangePasswordFormField].validateFunc(form, fieldValue);
    }
  
    function validatePassword(pw: string) {
      const regex = /^.{6,}$/;
      return regex.test(pw);
    }
  
    return (
      <Grid container spacing={2} style={{ marginTop: 10, marginBottom: 20, marginLeft: 25, marginRight: 25 }}>
        <Grid item xs={12}>
          <TextField
            error={form.oldPassword.isError}
            helperText={form.oldPassword.isError && form.oldPassword.reason}
            id="oldPassword"
            name="oldPassword"
            label={t("acc_key.pw_old")}
            defaultValue=""
            type="password"
            value={form.oldPassword.value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onFormChanged(e)}
            margin="normal"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
  
        <Grid item xs={12}>
          <TextField
            error={form.newPassword.isError}
            helperText={form.newPassword.isError && form.newPassword.reason}
            id="newPassword"
            name="newPassword"
            label={t("acc_key.pw_new")}
            type="password"
            defaultValue=""
            value={form.newPassword.value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onFormChanged(e)}
            margin="normal"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EnhancedEncryption />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
  
        <Grid item xs={12}>
          <TextField
            error={form.confirmNewPassword.isError}
            helperText={form.confirmNewPassword.isError && form.confirmNewPassword.reason}
            id="confirmNewPassword"
            name="confirmNewPassword"
            label={t("acc_key.pw_new_confirm")}
            type="password"
            defaultValue=""
            value={form.confirmNewPassword.value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onFormChanged(e)}
            margin="normal"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EnhancedEncryption />
                </InputAdornment>
              ),
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                onSubmitButtonClick()
              }
            }}
          />
        </Grid>
  
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={onSubmitButtonClick}
            disabled={isSubmiting}
            disableElevation
          >
            {t("setting.butt_submit")}
          </Button>
        </Grid>
  
      </Grid>
    )
  }

  const LinkFakeEmailPanel = (props: { email: string | null | undefined, triggerError: (variant: keyof typeof variantIcon, message: string) => void }) => {
    return (
      <Grid item md={12} sm={12} xs={12}>
        <GridCard title={t("setting.link_fake_email_panel_title")} desc={t("setting.link_fake_email_panel_desc")}>
          {
            (props.email)
              ?
              <div style={{ margin: 20 }}>{t("setting.link_fake_email_panel_content")}</div>
              :
              <div style={{ margin: 20 }}>
                <LinkFakeEmailForm email={props.email} triggerError={props.triggerError} />
              </div>
          }
        </GridCard>
      </Grid>
    )
  }

  const LinkFakeEmailForm = (props: { email: string | null | undefined, triggerError: (variant: keyof typeof variantIcon, message: string) => void }) => {

    type AddPasswordFormField = "newPassword" | "confirmNewPassword"
  
    const checkNewPassword = (form: AddPasswordFormInterface, value: string) => {
      if (!validatePassword(value)) {
        setForm(f => { return { ...f, newPassword: { ...f.newPassword, isError: true, reason: t("error.pw_format") } } });
        return false;
      }
      else {
        setForm(f => { return { ...f, newPassword: { ...f.newPassword, isError: false, reason: undefined } } });
        return true;
      }
    }
  
    const checkConfirmNewPassword = (form: AddPasswordFormInterface, value: string) => {
      if (!validatePassword(value)) {
        setForm(f => { return { ...f, confirmNewPassword: { ...f.confirmNewPassword, isError: true, reason: t("error.pw_format") } } });
        return false;
      }
      else if (value !== form.newPassword.value) {
        setForm(f => { return { ...f, confirmNewPassword: { ...f.confirmNewPassword, isError: true, reason: t("error.pw_confirm_not_match") } } });
        return false;
      }
      else {
        setForm(f => { return { ...f, confirmNewPassword: { ...f.confirmNewPassword, isError: false, reason: undefined } } });
        return true;
      }
    }
  
    const defaultAddPasswordForm = {
      newPassword: {
        value: "",
        isError: false,
        reason: undefined,
        validateFunc: checkNewPassword
      },
      confirmNewPassword: {
        value: "",
        isError: false,
        reason: undefined,
        validateFunc: checkConfirmNewPassword
      }
    };
  
    const [form, setForm] = useState<AddPasswordFormInterface>(defaultAddPasswordForm);
  
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
  
    type AddPasswordFormInterface = {
      newPassword: {
        value: string,
        isError: boolean,
        reason: string | undefined,
        validateFunc: (form: AddPasswordFormInterface, value: string) => boolean
      },
  
      confirmNewPassword: {
        value: string,
        isError: boolean,
        reason: string | undefined,
        validateFunc: (form: AddPasswordFormInterface, value: string) => boolean
      },
    }
  
    const onSubmitButtonClick = () => {
      const validateResult = Object.entries(form).map(([fieldId, fieldProps]) => {
        return fieldProps.validateFunc(form, fieldProps.value);
      });
  
      setIsSubmiting(true);
  
      if (validateResult.some(result => result === false)) {
        props.triggerError("error", t("setting.error_text_form_error"));
        setIsSubmiting(false);
        return;
      }
  
      if (firebaseAuth.currentUser !== undefined && firebaseAuth.currentUser !== null && firebaseAuth.currentUser?.phoneNumber) {
  
        // const recaptchaVerifier = new firebase.auth.RecaptchaVerifier("add-password-button", { 'size': 'invisible' }, firebase.app());
        const recaptchaVerifier = new RecaptchaVerifier("add-password-button", { 'size': 'invisible' }, firebaseAuth);
        recaptchaVerifier.render();
  
        reauthenticateWithPhoneNumber(firebaseAuth.currentUser!, firebaseAuth.currentUser!.phoneNumber!, recaptchaVerifier)
          .then((confirmationResult: any) => {
            return confirmationResult.confirm(prompt(t("setting.prompt_text")) ?? "");
          })
          .then((userCredential: any) => {
            const reauthCredential =
              props.triggerError("success", t("setting.success_text_checking"));
  
            try {
              linkFakeEmail(form.newPassword.value).then(() => {
                props.triggerError("success", t("setting.success_text_sucess"));
                setIsSubmiting(false);
                firebaseAuth.signOut();
              })
            }
            catch (e) {
              props.triggerError("error", t("setting.error_text_pw_update_error"));
              setIsSubmiting(false);
              return;
            }
          })
          .catch((error: any) => {
            props.triggerError("error", t("setting.error_text_checking_error"));
            setIsSubmiting(false);
            return;
          });
      }
      else {
        props.triggerError("error", t("setting.error_text_page_error"));
        return;
      }
    }
  
    const onFormChanged = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const fieldName = e.target.name;
      const fieldValue = e.target.value;
  
      setForm(f => { return { ...f, [fieldName]: { ...f[fieldName as AddPasswordFormField], value: fieldValue } } })
      form[fieldName as AddPasswordFormField].validateFunc(form, fieldValue);
    }
  
    function validatePassword(pw: string) {
      const regex = /^.{6,}$/;
      return regex.test(pw);
    }
  
    return (
      <Grid container spacing={2} style={{ marginTop: 10, marginBottom: 20, marginLeft: 25, marginRight: 25 }}>
        <Grid item xs={12}>
          <TextField
            error={form.newPassword.isError}
            helperText={form.newPassword.isError && form.newPassword.reason}
            id="newPassword"
            name="newPassword"
            label={t("acc_key.pw_new")}
            type="password"
            defaultValue=""
            value={form.newPassword.value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onFormChanged(e)}
            margin="normal"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EnhancedEncryption />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
  
        <Grid item xs={12}>
          <TextField
            error={form.confirmNewPassword.isError}
            helperText={form.confirmNewPassword.isError && form.confirmNewPassword.reason}
            id="confirmNewPassword"
            name="confirmNewPassword"
            label={t("acc_key.pw_new_confirm")}
            type="password"
            defaultValue=""
            value={form.confirmNewPassword.value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onFormChanged(e)}
            margin="normal"
            variant="outlined"
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                onSubmitButtonClick()
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EnhancedEncryption />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
  
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={onSubmitButtonClick}
            disabled={isSubmiting}
            disableElevation
            id="add-password-button"
          >
            {t("setting.butt_submit")}
          </Button>
        </Grid>
      </Grid>
    )
  }

  return (
    <div>
      {/* <AppBar title={"設定"} /> */}
      <SimpleSnackBar open={errorSnackBar.open} onClose={closeErrorSnackBar} message={errorSnackBar.message} variant="error" />
      {(loggedInUser) ?
        <StyledContainer style={{ paddingTop: 90, paddingBottom: 80 }}>
          <Grid container spacing={4}>
            <ChangePasswordPanel email={loggedInUser?.email} triggerError={(variant, message) => setErrorSnackBar({ ...errorSnackBar, variant: variant, open: true, message: message })} />
            <LinkFakeEmailPanel email={loggedInUser?.email} triggerError={(variant, message) => setErrorSnackBar({ ...errorSnackBar, variant: variant, open: true, message: message })} />
          </Grid>
        </StyledContainer>
        :
        <SimpleSnackBar open={true} onClose={closeErrorSnackBar} message={"You don't have permission to view this page."} variant="warning" autoClose={99999} />}
      {/* <AppBar title={"設定"} bottom={true} /> */}
    </div>
  )
}